import { useEffect, useState, useCallback } from 'react';
import { X, Loader2, User, Trophy, Clock, CheckCircle, AlertCircle, Brain, Calendar } from 'lucide-react';
import api from '../../services/api';
import RichContent from '../content/RichContent';

/**
 * QuizCodeReviewModal — Owner-only review of a finished quiz_code session.
 *
 * Props:
 *  - quizCodeId: int (quiz_codes.id)
 *  - onClose: () => void
 *
 * Renders a scrollable list of every question with:
 *   - The enonce
 *   - The 4 answers annotated :
 *     - correct answer : green background (always)
 *     - given answer if wrong : red border + slash
 *     - given answer if correct : double tick
 *   - The explication below
 *   - A "not answered" hint if the participant skipped the question
 */
export default function QuizCodeReviewModal({ quizCodeId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: res } = await api.get('/quiz-code/review.php', { params: { id: quizCodeId } });
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur de chargement');
        }
        setLoading(false);
    }, [quizCodeId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    const pct = data && data.total > 0 ? Math.round((data.score / data.total) * 100) : null;
    const mm = data ? Math.floor((data.time_in_seconds || 0) / 60) : 0;
    const ss = data ? (data.time_in_seconds || 0) % 60 : 0;

    return (
        <div className="qcr-overlay" onClick={onClose}>
            <div className="qcr-modal" onClick={e => e.stopPropagation()}>
                <div className="qcr-header">
                    <div className="qcr-header-main">
                        <span className="qcr-title">Revue de session</span>
                        {data && (
                            <span className="qcr-sub">
                                {data.sous_sujet?.label} — {data.sous_sujet?.categorie}
                            </span>
                        )}
                    </div>
                    <button className="qcr-close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
                </div>

                {loading && (
                    <div className="qcr-loading"><Loader2 size={18} className="spin" /> Chargement...</div>
                )}
                {error && (
                    <div className="qcr-error"><AlertCircle size={14} /> {error}</div>
                )}

                {data && !loading && (
                    <>
                        {/* Stats row */}
                        <div className="qcr-stats">
                            <span className="qcr-chip"><User size={12} /> {data.participant_name || '—'}</span>
                            {pct !== null && (
                                <span className={`qcr-chip qcr-chip--${pct >= 75 ? 'good' : pct >= 50 ? 'ok' : 'bad'}`}>
                                    <Trophy size={12} /> {data.score}/{data.total} ({pct}%)
                                </span>
                            )}
                            <span className="qcr-chip"><Clock size={12} /> {mm}m{ss.toString().padStart(2, '0')}</span>
                            <span className="qcr-chip"><Brain size={12} /> {data.mode === 'apprentissage' ? 'Apprentissage' : 'Classique'}</span>
                            {data.finished_at && (
                                <span className="qcr-chip"><Calendar size={12} /> {new Date(data.finished_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span>
                            )}
                            {data.label && <span className="qcr-chip qcr-chip--group">{data.label}</span>}
                        </div>

                        {/* Questions */}
                        <div className="qcr-questions">
                            {data.questions.map((q, idx) => {
                                const snap = data.answers_snapshot?.[q.id] || data.answers_snapshot?.[String(q.id)] || null;
                                const given = snap?.given ?? null;
                                const correct = q.bonne_reponse;
                                const notAnswered = given === null || given === undefined;
                                const isCorrect = !notAnswered && given === correct;

                                return (
                                    <div key={q.id} className={`qcr-q${isCorrect ? ' qcr-q--correct' : notAnswered ? ' qcr-q--skipped' : ' qcr-q--wrong'}`}>
                                        <div className="qcr-q-head">
                                            <span className="qcr-q-num">#{idx + 1}</span>
                                            {isCorrect ? <CheckCircle size={16} color="#22c55e" /> : notAnswered ? <AlertCircle size={16} color="#94a3b8" /> : <AlertCircle size={16} color="#ef4444" />}
                                            <span className="qcr-q-status">
                                                {isCorrect ? 'Correct' : notAnswered ? 'Non répondu' : 'Incorrect'}
                                            </span>
                                        </div>

                                        <div className="qcr-q-enonce">
                                            <RichContent blocks={q.enonce} />
                                        </div>

                                        <div className="qcr-q-answers">
                                            {q.reponses.map(r => {
                                                const isGiven = given === r.id;
                                                const isRight = correct === r.id;
                                                let cls = 'qcr-ans';
                                                if (isRight) cls += ' qcr-ans--correct';
                                                if (isGiven && !isRight) cls += ' qcr-ans--wrong-given';
                                                if (isGiven && isRight) cls += ' qcr-ans--right-given';
                                                return (
                                                    <div key={r.id} className={cls}>
                                                        <span className="qcr-ans-id">{r.id.toUpperCase()}</span>
                                                        <div className="qcr-ans-body"><RichContent blocks={r.content} /></div>
                                                        <span className="qcr-ans-flag">
                                                            {isGiven && isRight && <CheckCircle size={14} />}
                                                            {isGiven && !isRight && <X size={14} />}
                                                            {!isGiven && isRight && <span style={{ fontSize: '0.72em', color: '#22c55e', fontWeight: 700 }}>bonne réponse</span>}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {Array.isArray(q.explication) && q.explication.length > 0 && (
                                            <div className="qcr-q-explication">
                                                <div className="qcr-q-explication-label">Explication</div>
                                                <RichContent blocks={q.explication} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
